import { z } from 'zod';
import { MANDATORY_EVIDENCE, PRIORITIES, SUITE_TYPES, TEST_CASE_TYPES, TEST_RESULT_STATUSES, SEVERITIES } from '../enums';
import { isoDate, uuid } from './common';
import { itemKey } from './work-items';

export const testFolderSchema = z.object({ name: z.string().min(1).max(120), parentId: uuid.nullable().optional() });
export type TestFolderInput = z.infer<typeof testFolderSchema>;

export const testStepSchema = z.object({ step: z.string().min(1).max(4000), expected: z.string().max(4000).default('') });
export type TestStep = z.infer<typeof testStepSchema>;

export const createTestCaseSchema = z.object({
  title: z.string().min(1).max(255),
  folderId: uuid.nullable().optional(),
  preconditions: z.string().max(8000).optional(),
  steps: z.array(testStepSchema).max(200).default([]),
  priority: z.enum(PRIORITIES).default('medium'),
  isCritical: z.boolean().default(false),
  type: z.enum(TEST_CASE_TYPES).default('manual'),
  automationId: z.string().max(200).nullable().optional(),
  tags: z.array(z.string().min(1).max(40)).max(30).default([]),
  componentId: uuid.nullable().optional(),
  coverageKeys: z.array(itemKey).max(50).optional(),
});
export type CreateTestCaseInput = z.infer<typeof createTestCaseSchema>;

/** Editing steps/preconditions creates a new case version (FR-TST-2); metadata edits do not. */
export const updateTestCaseSchema = createTestCaseSchema.omit({ coverageKeys: true }).partial().extend({ changeNote: z.string().max(500).optional() });
export type UpdateTestCaseInput = z.infer<typeof updateTestCaseSchema>;

export const caseFilterSchema = z.object({
  folderIds: z.array(uuid).optional(),
  tags: z.array(z.string()).optional(),
  priorities: z.array(z.enum(PRIORITIES)).optional(),
  criticalOnly: z.boolean().optional(),
  types: z.array(z.enum(TEST_CASE_TYPES)).optional(),
  componentIds: z.array(uuid).optional(),
  text: z.string().max(200).optional(),
});
export type CaseFilter = z.infer<typeof caseFilterSchema>;

export const testSuiteSchema = z.object({
  name: z.string().min(1).max(120),
  suiteType: z.enum(SUITE_TYPES),
  mode: z.enum(['static', 'dynamic']).default('static'),
  filter: caseFilterSchema.default({}),
  description: z.string().max(2000).nullable().optional(),
});
export type TestSuiteInput = z.infer<typeof testSuiteSchema>;

export const suiteCasesSchema = z.object({ caseIds: z.array(uuid).max(5000) });

/** Exit criteria use the 4.8.1 names explicitly (CORRECTIONS #4). */
export const exitCriteriaSchema = z.object({
  progress: z.number().min(0).max(1).default(1),
  effective: z.number().min(0).max(1).default(0.95),
  critical: z.number().min(0).max(1).default(1),
  maxOpenBlockers: z.number().int().min(0).default(0),
});
export type ExitCriteria = z.infer<typeof exitCriteriaSchema>;

export const testEnvironmentSchema = z.object({
  id: uuid.optional(),
  name: z.string().min(1).max(120),
  attributes: z.record(z.string(), z.string()).default({}),
});

export const testPlanSchema = z.object({
  name: z.string().min(1).max(160),
  suiteIds: z.array(uuid).default([]),
  environments: z.array(testEnvironmentSchema).min(1).max(50),
  exitCriteria: exitCriteriaSchema.default({}),
  schedule: z.object({ start: isoDate.optional(), end: isoDate.optional() }).default({}),
  mandatoryEvidence: z.enum(MANDATORY_EVIDENCE).default('critical'),
  description: z.string().max(4000).nullable().optional(),
});
export type TestPlanInput = z.infer<typeof testPlanSchema>;

/** One run per (suite × environment) against an exact build (FR-TST-6/7). */
export const generateRunsSchema = z.object({
  buildId: uuid,
  suiteIds: z.array(uuid).optional(),
  environmentIds: z.array(uuid).optional(),
  assignments: z.array(z.object({ environmentId: uuid, userId: uuid })).default([]),
  /** Attach an existing QA task instead of auto-creating one per run. */
  qaTaskKey: itemKey.optional(),
});
export type GenerateRunsInput = z.infer<typeof generateRunsSchema>;

export const recordResultSchema = z.object({
  status: z.enum(TEST_RESULT_STATUSES),
  notes: z.string().max(8000).nullable().optional(),
  evidenceAttachmentIds: z.array(uuid).max(50).optional(),
  durationSec: z.number().int().min(0).max(86400).optional(),
});
export type RecordResultInput = z.infer<typeof recordResultSchema>;

/** Multi-select failed results → one bug, or link to an existing bug (FR-TST-10/11). */
export const resultDefectsSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('create'),
    resultIds: z.array(uuid).min(1).max(200),
    title: z.string().min(1).max(255),
    severity: z.enum(SEVERITIES).default('major'),
    priority: z.enum(PRIORITIES).default('high'),
    description: z.string().max(10000).optional(),
  }),
  z.object({ mode: z.literal('link'), resultIds: z.array(uuid).min(1).max(200), bugKey: itemKey }),
]);
export type ResultDefectsInput = z.infer<typeof resultDefectsSchema>;

export const coverageSchema = z.object({ storyKeys: z.array(itemKey).max(100) });

export const linkWorklogResultsSchema = z.object({ resultIds: z.array(uuid).min(1).max(500) });
